import { useEffect, useState } from 'react';
import { WifiOff, RefreshCcw } from 'lucide-react';

/**
 * <PWABanners>
 *
 * Mounts two small status pills bottom-right:
 *  - "Offline" — shown when `navigator.onLine === false`. The SW still
 *    serves cached API data; this banner just signals the state.
 *  - "Update available — reload to apply" — fires from registerSW.js when
 *    a new service worker is installed (`updatefound` event). Click reloads
 *    the tab to activate the new SW.
 */
export default function PWABanners() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [updateReady, setUpdateReady] = useState(false);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    const handleUpdate = () => setUpdateReady(true);
    window.addEventListener('polkdesq:sw-update', handleUpdate);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('polkdesq:sw-update', handleUpdate);
    };
  }, []);

  if (online && !updateReady) return null;

  return (
    <div
      data-testid="pwa-banners"
      className="fixed bottom-4 right-4 z-[120] flex flex-col gap-2 items-end pointer-events-none"
    >
      {!online ? (
        <div
          data-testid="pwa-offline-banner"
          role="status"
          aria-live="polite"
          className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full text-[11px] font-semibold"
          style={{
            background: 'var(--pd-warning-soft)',
            color: 'var(--pd-warning-base)',
            border: '1px solid var(--pd-warning-base)',
            backdropFilter: 'blur(20px) saturate(160%)',
            WebkitBackdropFilter: 'blur(20px) saturate(160%)',
          }}
        >
          <WifiOff className="w-3 h-3" />
          Offline — showing last-known state
        </div>
      ) : null}

      {updateReady ? (
        <button
          data-testid="pwa-update-banner"
          type="button"
          onClick={() => window.location.reload()}
          className="pointer-events-auto flex items-center gap-2 px-3 py-2 rounded-full text-[11px] font-semibold"
          style={{
            background: 'var(--pd-surface-3)',
            color: 'var(--pd-text-primary)',
            border: '1px solid var(--pd-border-highlight)',
            boxShadow: 'var(--pd-shadow-float), var(--pd-edge-highlight)',
            backdropFilter: 'blur(28px) saturate(160%)',
            WebkitBackdropFilter: 'blur(28px) saturate(160%)',
          }}
        >
          <RefreshCcw className="w-3 h-3" style={{ color: 'var(--pd-focus)' }} />
          New version available — click to reload
        </button>
      ) : null}
    </div>
  );
}
